/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * ThemeTutorial Component
 * ============================================================================
 * 
 * A tutorial modal/tooltip shown on first visit to guide users about
 * theme switching capabilities.
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { X, Sun, Moon, Monitor, Sparkles } from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { useTranslation } from 'react-i18next';

interface ThemeTutorialProps {
  /** Position of the tutorial relative to the toggle */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Delay before showing (ms) */
  showDelay?: number;
}

export function ThemeTutorial({
  position = 'bottom',
  showDelay = 1000
}: ThemeTutorialProps) {
  const { t } = useTranslation('common');
  const { isFirstVisit, completeTutorial, isDark, setTheme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isFirstVisit) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, showDelay);

    return () => clearTimeout(timer);
  }, [isFirstVisit, showDelay]);

  const handleClose = () => {
    setIsVisible(false);
    completeTutorial();
  };

  const handleTryTheme = (theme: 'dark' | 'light' | 'system') => {
    setTheme(theme);
    setStep(prev => prev + 1);
  };

  if (!isVisible) return null;

  const positionClasses = {
    top: 'bottom-full mb-3 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-3 left-1/2 -translate-x-1/2',
    left: 'right-full mr-3 top-1/2 -translate-y-1/2',
    right: 'left-full ml-3 top-1/2 -translate-y-1/2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-[var(--card-bg)] border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-[var(--card-bg)] border-l-transparent border-r-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-[var(--card-bg)] border-t-transparent border-b-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-[var(--card-bg)] border-t-transparent border-b-transparent border-l-transparent',
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
        onClick={handleClose}
      />

      {/* Tutorial Card */}
      <div 
        className={`
          absolute z-50
          ${positionClasses[position]}
          w-80
          animate-slide-up
        `}
      >
        {/* Arrow */}
        <div 
          className={`
            absolute w-0 h-0
            border-8
            ${arrowClasses[position]}
          `}
        />

        <div
          className="
            bg-[var(--card-bg)]
            border border-[var(--card-border)]
            rounded-xl
            shadow-xl
            overflow-hidden
          "
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border-subtle)]">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[var(--color-brand-primary)]" />
              <span className="font-heading font-semibold text-sm">{t('themeTutorial.title')}</span>
            </div>
            <button
              onClick={handleClose}
              className="
                p-1 rounded-md
                text-[var(--color-text-muted)]
                hover:text-[var(--color-text-primary)]
                hover:bg-[var(--color-bg-hover)]
                transition-colors
              "
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {step === 0 && (
              <div className="space-y-4">
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {t('themeTutorial.welcome')}{' '}
                  <strong className="text-[var(--color-text-primary)]">
                    {isDark ? t('themeTutorial.darkMode') : t('themeTutorial.lightMode')}
                  </strong>.
                </p>

                <div className="flex gap-2">
                  <ThemeButton
                    icon={<Moon className="w-4 h-4" />}
                    label={t('themeTutorial.themes.dark')}
                    isActive={isDark}
                    onClick={() => handleTryTheme('dark')}
                  />
                  <ThemeButton
                    icon={<Sun className="w-4 h-4" />}
                    label={t('themeTutorial.themes.light')}
                    isActive={!isDark}
                    onClick={() => handleTryTheme('light')}
                  />
                  <ThemeButton
                    icon={<Monitor className="w-4 h-4" />}
                    label={t('themeTutorial.themes.system')}
                    isActive={false}
                    onClick={() => handleTryTheme('system')}
                  />
                </div>

                <p className="text-xs text-[var(--color-text-muted)]">
                  {t('themeTutorial.tryClickHint')}
                </p>
              </div>
            )}

            {step >= 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-[var(--color-success)]">
                  <span className="text-lg">✓</span>
                  <span className="text-sm font-medium">{t('themeTutorial.themeChanged')}</span>
                </div>

                <p className="text-sm text-[var(--color-text-secondary)]">
                  {t('themeTutorial.savedMessage')}
                </p>

                <button
                  onClick={handleClose}
                  className="
                    w-full
                    btn btn-primary
                    text-sm
                  "
                >
                  {t('themeTutorial.gotIt')}
                </button>
              </div>
            )}
          </div>

          {/* Footer with step indicator */}
          {step === 0 && (
            <div className="px-4 py-3 border-t border-[var(--color-border-subtle)] bg-[var(--color-bg-tertiary)]">
              <button
                onClick={handleClose}
                className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
              >
                {t('themeTutorial.skip')} →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}


/**
 * Theme Button for Tutorial
 */
interface ThemeButtonProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function ThemeButton({ icon, label, isActive, onClick }: ThemeButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`
        flex-1
        flex flex-col items-center gap-1
        px-3 py-2
        rounded-lg
        border
        transition-all duration-200
        ${isActive
          ? 'border-[var(--color-brand-primary)] bg-[var(--sidebar-item-active)] text-[var(--color-brand-primary)]'
          : 'border-[var(--color-border-default)] bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)] hover:text-[var(--color-text-primary)]'
        }
      `}
    >
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}


export default ThemeTutorial;
